import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { Button } from '@worldcoo/ui/components/button';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { toast } from '@/lib/toast';
import { DonatingIllustration } from '@worldcoo/ui/custom/donating-illustration';
import { useAppForm } from '@/lib/use-app-form';
import { NgoLanguageSelect } from './-components/ngo-language-select';
import { generateCaptchaTokenResult } from '@/lib/captcha';
import { i18n } from '@lingui/core';
import { useEffect, useState } from 'react';
import * as v from 'valibot';
import * as z from 'zod';

export const Route = createFileRoute('/ngos/login')({
	validateSearch: v.object({
		redirect: v.fallback(v.optional(v.string()), undefined),
		email: v.fallback(v.optional(v.string()), undefined),
		error: v.fallback(v.optional(v.picklist(['expired', 'invalid'])), undefined),
	}),
	component: LoginPage,
});

function LoginPage() {
	const { t } = useLingui();
	const navigate = useNavigate();
	const { redirect, email: prefilledEmail, error: initialLinkError } = Route.useSearch();
	const [sentToEmail, setSentToEmail] = useState<string | null>(null);
	// Snapshotted, so the URL can drop it without the banner returning on back navigation.
	const [linkError] = useState(initialLinkError ?? null);

	useEffect(() => {
		if (initialLinkError) {
			navigate({ to: '/ngos/login', search: (prev) => ({ ...prev, error: undefined }), replace: true });
		}
	}, [initialLinkError, navigate]);

	const linkErrorMessage =
		linkError === 'expired'
			? t`This login link has expired. Please request a new one.`
			: linkError === 'invalid'
				? t`This login link is invalid. Please request a new one.`
				: null;

	const { mutateAsync: requestLogin, isPending } = useMutation(
		trpc.auth.requestLogin.mutationOptions({
			onSuccess: (result, variables) => {
				if ('captchaPassed' in result && !result.captchaPassed) {
					toast.error(t`Captcha verification failed. Please try again.`);
					return;
				}
				setSentToEmail(variables.email);
			},
			onError: () => {
				toast.error(t`Something went wrong. Please try again.`);
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			email: prefilledEmail ?? '',
		},
		validators: {
			onSubmit: z.object({
				email: z.email(t`Invalid email`),
			}),
		},
		onSubmit: async ({ value }) => {
			if (!value.email.trim()) return;
			const { data: captchaToken } = await generateCaptchaTokenResult();
			if (!captchaToken) {
				toast.error(t`Captcha verification failed. Please try again.`);
				return;
			}
			try {
				await requestLogin({ email: value.email.trim(), locale: i18n.locale || undefined, captchaToken, redirect });
			} catch {
				// onError already toasts; an escaped rejection would be ticketed.
			}
		},
	});

	return (
		<div
			data-page="login"
			className="relative flex min-h-screen bg-white"
			style={{ viewTransitionName: 'ngo-main-content' }}
		>
			<div className="absolute bottom-2 left-2 px-4 py-2 md:bottom-6 md:left-6 md:px-12 md:py-6">
				<NgoLanguageSelect />
			</div>
			<div className="flex flex-1 flex-col p-6 sm:p-10 lg:p-20">
				<WorldcooLogo accentClassName="text-[#f28e41]" className="h-12 w-auto self-start md:h-[80px]" />

				<div className="flex flex-1 items-center pb-16 md:pb-0">
					<div className="flex w-full max-w-[459px] flex-col gap-8">
						{sentToEmail ? (
							<div className="animate-in fade-in-0 slide-in-from-bottom-4 flex flex-col gap-2 text-[#35433f] duration-500 ease-out motion-reduce:animate-none">
								<h1 className="animate-in fade-in-0 slide-in-from-bottom-3 text-2xl leading-tight font-medium tracking-[-0.2px] duration-500 ease-out [animation-delay:60ms] [animation-fill-mode:both] motion-reduce:animate-none md:text-[32px] md:leading-[38px]">
									{t`Check your email`}
								</h1>
								<p className="animate-in fade-in-0 slide-in-from-bottom-3 text-base leading-normal duration-500 ease-out [animation-delay:160ms] [animation-fill-mode:both] motion-reduce:animate-none">
									{t`We've sent an email to ${sentToEmail}. Open the email and click the link to sign in. It expires in 15 minutes.`}
								</p>
								<p className="animate-in fade-in-0 slide-in-from-bottom-3 text-base leading-normal font-bold duration-500 ease-out [animation-delay:260ms] [animation-fill-mode:both] motion-reduce:animate-none">
									{t`You can safely close this page.`}
								</p>
							</div>
						) : (
							<>
								<div className="flex flex-col gap-2 text-[#35433f]">
									<h1 className="text-2xl leading-tight font-medium tracking-[-0.2px] md:text-[32px] md:leading-[38px]">
										{t`Log in into your account`}
									</h1>
									<p className="text-base leading-normal">{t`Enter your email and we'll send you a login link`}</p>
								</div>

								{linkErrorMessage && (
									<div
										role="alert"
										className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
									>
										{linkErrorMessage}
									</div>
								)}

								<form
									onSubmit={(e) => {
										e.preventDefault();
										form.handleSubmit();
									}}
									className="flex flex-col gap-6"
								>
									<form.AppField
										name="email"
										children={(field) => (
											<field.TextField
												label={t`Username`}
												placeholder={t`Enter your email`}
												className="[&_input]:h-10"
											/>
										)}
									/>

									<Button type="submit" className="h-10 w-fit" disabled={isPending}>
										{isPending ? t`Sending...` : t`Send login link to email`}
									</Button>
								</form>
							</>
						)}
					</div>
				</div>
			</div>

			<div className="hidden w-1/2 p-6 pl-0 lg:block">
				<div className="flex h-full items-center justify-center rounded-lg bg-[#fff7ed]">
					<DonatingIllustration className="text-primary -mr-18 size-[599px]" />
				</div>
			</div>
		</div>
	);
}
